import moment from 'moment';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import Skeleton from 'react-loading-skeleton';
import { Bounce, toast } from 'react-toastify';
import { fetchCampaignApplySocialPosts } from '../../services/api/campaign';
import { fetchLatestInfluencerSocialPosts } from '../../services/api/influencer';
import { SocialPost } from '../../types/collaborations/CollaborationTypes';
import { SocialAccountData } from '../../types/influencer/influencerTypes';
import { formatNumber, getErrorMessage, retryUntil, toValidNumber } from '../../utils/helpers';
import withSlideUpModal from '../../utils/hoc/withSlideUpModalComponent';
import './CampaignSocialPostSelectionModal.scss';

interface CampaignSocialPostSelectionProps {
    socialAccount: SocialAccountData;
    onContinue: (socialPostId: string) => void;
    onClose: () => void;
}

const CampaignSocialPostSelection: React.FC<CampaignSocialPostSelectionProps> = ({
    socialAccount,
    onContinue,
}) => {
    const socialAccountId = socialAccount.social_account.uuid;
    const socialAccountPlatform = socialAccount.social_account.platform;
    const [selectedPostId, setSelectedPostId] = useState<string | null>(null);
    const [posts, setPosts] = useState<SocialPost[]>([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [fetchingLatestMedia, setFetchingLatestMedia] = useState(false);
    const [lastFetchedAt, setLastFetchedAt] = useState<string | null>(null);
    const fetchIdRef = useRef(0);

    const loadPosts = useCallback(async (pageNumber: number, replace = false) => {
        const fetchId = ++fetchIdRef.current;
        setLoading(true);
        try {
            const data = await fetchCampaignApplySocialPosts(socialAccountId, socialAccountPlatform, pageNumber);
            if (fetchId !== fetchIdRef.current) return;

            const items = data.items ?? [];
            setLastFetchedAt(data.last_fetched_at ?? null);
            setPosts((prev) => {
                const next = replace ? items : [...prev, ...items];
                setHasMore(next.length < (data.count ?? 0));
                return next;
            });
            setPage(pageNumber);
        } catch {
            if (fetchId !== fetchIdRef.current) return;
            if (replace) {
                setPosts([]);
                setHasMore(false);
            }
        } finally {
            if (fetchId === fetchIdRef.current) {
                setLoading(false);
            }
        }
    }, [socialAccountId, socialAccountPlatform]);

    const fetchLatestMedia = async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
        if (fetchingLatestMedia) return;
        e.stopPropagation();
        try {
            setFetchingLatestMedia(true);
            const loadingToastId = toast('Fetching latest media', {
                position: 'top-center',
                autoClose: 32000,
                closeOnClick: false,
                pauseOnHover: false,
                draggable: false,
                hideProgressBar: false,
                theme: 'colored',
                transition: Bounce,
                type: 'info',
                isLoading: true,
                closeButton: false,
            });
            await fetchLatestInfluencerSocialPosts(socialAccountId);
            const prev = lastFetchedAt;
            await retryUntil(async () => {
                const data = await fetchCampaignApplySocialPosts(socialAccountId, socialAccountPlatform, 1);
                const latest = data.last_fetched_at ?? null;
                if (latest !== prev) {
                    const items = data.items ?? [];
                    setLastFetchedAt(latest);
                    setPosts(items);
                    setHasMore(items.length < (data.count ?? 0));
                    setPage(1);
                    return true;
                }
                return false;
            });

            toast.update(loadingToastId, {
                type: 'success',
                isLoading: false,
                render: 'Showing latest media',
                autoClose: 2000,
            });
        } catch (error: unknown) {
            console.log(error);
            toast.error(getErrorMessage(error));
        } finally {
            setFetchingLatestMedia(false);
        }
    };

    useEffect(() => {
        setSelectedPostId(null);
        setPosts([]);
        setPage(1);
        setHasMore(true);
        setLastFetchedAt(null);
        loadPosts(1, true);
    }, [socialAccountId, loadPosts]);

    const nextPage = () => {
        if (hasMore && !loading) {
            loadPosts(page + 1);
        }
    };

    const handleContinue = () => {
        if (!selectedPostId) return;
        onContinue(selectedPostId);
    };

    return (
        <div className="campaign-social-post-selection">
            <div className="campaign-social-post-selection__header">
                <div className="campaign-social-post-selection__header-row">
                    <div>
                        <h2 className="campaign-social-post-selection__title">Select best creative</h2>
                        <p className="campaign-social-post-selection__subtitle">
                            Pick your best creative and show brands what makes your content stand out.
                        </p>
                    </div>
                    <button
                        type="button"
                        className="btn btn-xxs ms-2 btn-primary rounded-sm flex-shrink-0"
                        title="Fetch Latest Media"
                        disabled={fetchingLatestMedia}
                        onClick={fetchLatestMedia}
                    >
                        <i className={`fas fa-sync-alt ${fetchingLatestMedia ? 'fa-spin' : ''}`}></i>
                    </button>
                </div>
            </div>

            <div className="campaign-social-post-selection__grid-scroll" id="campaignSocialPostScroll">
                <InfiniteScroll
                    className="campaign-social-post-selection__grid"
                    hasMore={hasMore}
                    next={nextPage}
                    dataLength={posts.length}
                    loader={<></>}
                    scrollableTarget="campaignSocialPostScroll"
                >
                    {posts.map((post) => {
                        const isSelected = selectedPostId === post.uuid;
                        const thumb = post.thumbnail_url;
                        const postedLabel = `${post.date} ${post.month}`

                        return (
                            <div
                                className={`campaign-social-post-selection__grid-item ${isSelected ? 'is-selected' : ''}`}
                                key={post.uuid}
                            >
                                <span className="campaign-social-post-selection__select-indicator" aria-hidden />
                                <button
                                    type="button"
                                    className="campaign-social-post-selection__card"
                                    onClick={() => setSelectedPostId(post.uuid)}
                                    style={thumb ? { backgroundImage: `url(${thumb})` } : undefined}
                                    aria-label="Select creative"
                                    aria-pressed={isSelected}
                                >
                                    {!thumb && <span className="campaign-social-post-selection__card-fallback" />}
                                    {postedLabel && (
                                        <span className="campaign-social-post-selection__card-date">{postedLabel}</span>
                                    )}
                                    <span className="campaign-social-post-selection__card-stats">
                                        <span className="campaign-social-post-selection__stat">
                                            <i className="fa-solid fa-eye" aria-hidden />
                                            {formatNumber(toValidNumber(post.views_count))}
                                        </span>
                                    </span>
                                </button>
                            </div>
                        );
                    })}

                    {loading &&
                        Array.from({ length: 6 }).map((_, i) => (
                            <div className="campaign-social-post-selection__grid-item" key={`sk-${i}`}>
                                <Skeleton className="campaign-social-post-selection__skeleton" />
                            </div>
                        ))}
                </InfiniteScroll>

                {!loading && !posts.length && (
                    <div className="campaign-social-post-selection__empty">No content found on this account.</div>
                )}
            </div>

            <div className="campaign-social-post-selection__footer">
                <button
                    type="button"
                    className="btn btn-primary w-100 text-uppercase"
                    disabled={!selectedPostId || fetchingLatestMedia}
                    onClick={handleContinue}
                >
                    Continue
                </button>
            </div>
        </div>
    );
};

export default withSlideUpModal(CampaignSocialPostSelection, '', true);
